import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Clock, MessageCircle, MapPin, Heart, Check, ArrowRight } from 'lucide-react';

const iconMap = {
  Calendar: Calendar,
  Clock: Clock,
  MessageCircle: MessageCircle,
  MapPin: MapPin,
  Heart: Heart,
};

const ServiceDetailModal = ({ isOpen, onClose, service }) => {
  const navigate = useNavigate();
  
  if (!isOpen || !service) return null;

  const IconComponent = iconMap[service.icon];
  const isCelebranteService = service.title?.includes('Celebrante');

  const handleLearnMore = () => {
    onClose();
    navigate('/celebrante');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#2C2C2C]/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-[#FAF9F7] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#8B7D6B] hover:text-[#2C2C2C] transition-colors z-10"
          aria-label="Fechar"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#D4C4B0]/40 to-[#B5C4B1]/30 px-8 pt-8 pb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#FAF9F7] flex items-center justify-center shadow-md">
              <IconComponent size={24} className="text-[#6B4E37]" />
            </div>
            <div>
              <p className="text-caption text-[#8B7D6B] mb-1">
                Serviço
              </p>
              <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#2C2C2C]">
                {service.title}
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[60vh]">
          {/* Description */}
          <p className="text-body text-[#6B4E37] mb-8">
            {service.description}
          </p>

          {/* Divider */}
          <div className="divider !mx-0 !ml-0 mb-8" />

          {/* Features with descriptions */}
          <h4 className="text-caption text-[#C4B5A0] mb-6">
            O que está incluído
          </h4>
          
          <div className="space-y-6">
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="flex gap-4 p-4 bg-[#F5F3F0] rounded-lg hover:bg-[#D4C4B0]/20 transition-colors"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center mt-0.5">
                  <Check size={16} className="text-[#6B4E37]" />
                </div>
                <div>
                  <h5 className="font-['Cormorant_Garamond'] text-lg text-[#2C2C2C] mb-1">
                    {feature.title}
                  </h5>
                  <p className="text-sm text-[#8B7D6B] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-8 pt-6 border-t border-[#D4C4B0]/30 text-center">
            {isCelebranteService && (
              <button
                onClick={handleLearnMore}
                className="inline-flex items-center gap-2 px-6 py-3 mb-4 bg-[#B5C4B1] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-all duration-300"
                data-testid="learn-more-celebrante-btn"
              >
                Descobrir mais sobre Cerimónias Simbólicas
                <ArrowRight size={18} />
              </button>
            )}
            <p className="text-sm text-[#8B7D6B] mb-4">
              Interessado(a) neste serviço?
            </p>
            <button
              onClick={() => {
                onClose();
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="btn-primary"
            >
              Pedir mais informações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailModal;
